export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
import { Context, getOwner, runWithOwner, useContext } from 'solid-js';

export const nbsp = '\u00A0';

export function uuidv4() {
    // crypto is only available in a secure context, we fallback to Math.random if it's not available
    if (crypto && crypto.randomUUID) {
        return crypto.randomUUID();
    }

    const rand = (c: number) => (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4);
    return ('00000000-0000-4000-0000-000000000000').replace(/[0]/g, function (c) {
        return rand(Number(c)).toString(16).toUpperCase();
    });
}

export function delay(ms: number) : Promise<void> {
    return new Promise((res, err) => {
        setTimeout(() => {
            res();
        }, ms)
    });
}

/**
 * Allows exporting internal functionality to the legacy ST object.
 * @param data A dictionary of values to merge into window.ST
 */
export function exportForST(data: Record<string, unknown>) {
    const st = (window as any).ST ?? {};
    (window as any).ST = deepMerge(st, data);
}

export function deepMerge(target: any, source: any) {
    for (const key in source) {
        if (source[key] instanceof Object && key in target) {
            Object.assign(source[key], deepMerge(target[key], source[key]))
        }
    }
    Object.assign(target || {}, source)
    return target
}

export function requireContext<T>(context: Context<T|undefined>): T {
    const res = useContext(context);
    if (!res) {
        throw new Error('Context not found. Make sure you are using the component within a Provider or wrap your handler in captureContext()');
    }
    return res;
}

export const captureContext = <T, U extends any[]>(fn: (...args: U) => T) => {
    const owner = getOwner();
    if (!owner) console.error('No owner found. Make sure to call captureContext() within a component or a handler called by a component.');
    return (...args: U) => runWithOwner(owner, () => fn(...args));
}

type DebouncedFunction<F extends (...args: any[]) => any> = ((...args: Parameters<F>) => void) & { abort: () => void };

export function debounce<F extends (...args: any[]) => any>(func: F, delay: number): DebouncedFunction<F> {
    let timeout: number | null = null;

    function abort() {
        if (timeout !== null) {
            clearTimeout(timeout);
        }
    }

    const res = (...args: Parameters<F>) => {
        const later = () => {
            timeout = null;
            func(...args);
        };

        abort();
        timeout = window.setTimeout(later, delay);
    };

    res.abort = abort;

    return res as DebouncedFunction<F>;
}

export const toKebabCase = (str: string) => {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
}

export function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

export function getNamespace(nsName: string, obj?: any) {
    let ns = obj || window;
    const nsArr = nsName.split('.');

    while (nsArr.length > 0) {
        const name = nsArr.shift()!;
        ns[name] = ns[name] || {};
        ns = ns[name];
    }

    return ns;
}